import * as React from "react";
import * as url from "url";
import styled, { css } from "styled-components";
import { rgba } from "polished";
import { px2rem } from "common/helpers/rem";
// components
import ShavedText from "common/components/shavedText";
import Icon, { PACKS } from "common/icons";
import { POSITION_TYPE } from "common/components/thread/components/threadInput/index";

const ThreadInputFixedBottom = css`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${props => props.theme.white100};
  z-index: ${props => props.theme.zPopover};
  padding: ${px2rem(5)} ${px2rem(16)}
    calc(${px2rem(5)} + env(safe-area-inset-bottom));
  box-shadow: 0 ${px2rem(2)} ${px2rem(8)} 0
    ${props => rgba(props.theme.grey1000, 0.2)};
`;

const ThreadInputSticky = css`
  position: sticky;
  bottom: 0;
  z-index: ${props => props.theme.zPopover};
  padding: ${px2rem(5)} ${px2rem(16)}
    calc(${px2rem(5)} + env(safe-area-inset-bottom));
  border-top: ${px2rem(1)} solid ${props => props.theme.grey200};
  border-bottom: ${px2rem(1)} solid ${props => props.theme.grey200};
  background-color: ${props => props.theme.white100};
`;

function getInputPositionStyle(positionType?: POSITION_TYPE) {
  switch (positionType) {
    case POSITION_TYPE.FIXED_BOTTOM: {
      return ThreadInputFixedBottom;
    }

    case POSITION_TYPE.STICKY: {
      return ThreadInputSticky;
    }

    case POSITION_TYPE.STATIC:
    default: {
      return;
    }
  }
}

export const ThreadInputContainer = styled.form<{
  positionType?: POSITION_TYPE;
}>`
  padding: 0 ${px2rem(16)};
  display: flex;
  align-items: flex-end;
  ${({ positionType }) => getInputPositionStyle(positionType)}
`;
ThreadInputContainer.displayName = "ThreadInputContainer";

export const ThreadInputMedia = styled.button.attrs({
  type: "button",
})`
  width: ${px2rem(24)};
  height: ${px2rem(38)};
  margin-right: ${px2rem(8)};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;
ThreadInputMedia.displayName = "ThreadInputMedia";

export const ThreadInputContentWrapper = styled.div`
  box-sizing: border-box;
  flex: 1;
  border-radius: ${px2rem(19)};
  min-height: ${px2rem(38)};
  background-color: ${props => props.theme.white100};
  padding-left: ${px2rem(12)};
`;
ThreadInputContentWrapper.displayName = "ThreadInputContent";

export const ThreadInputContent = styled.div`
  display: flex;

  .ThreadInput {
    flex: 1;
    width: 100%;
    height: ${px2rem(38)};
    padding: ${px2rem(11)} ${px2rem(12)} ${px2rem(11)} 0;
    border: none;
    box-sizing: border-box;
    font-size: ${px2rem(12)};
    line-height: 1.33;
    font-weight: ${props => props.theme.weightRegular};
    background-color: transparent;
    color: ${props => props.theme.grey1000};
    resize: none;

    &::placeholder {
      color: ${props => props.theme.grey500};
    }
  }
`;

export const ThreadInputSubmit = styled.button.attrs({
  type: "submit",
})`
  width: ${px2rem(40)};
  height: ${px2rem(38)};
  display: flex;
  align-items: center;
  justify-content: center;
  &:disabled {
    display: none;
  }
`;
ThreadInputSubmit.displayName = "ThreadInputSubmit";

export const UploadedMediaWrapper = styled.div`
  border-radius: 6px;
  overflow: hidden;
  position: relative;
  background-color: ${props => props.theme.white100};
  min-height: ${px2rem(56)};
  margin: ${px2rem(11)} ${px2rem(12)} 0 0;
`;

export const UploadedImage = styled.div`
  .gifIcon {
    position: absolute;
    right: ${px2rem(4)};
    bottom: ${px2rem(4)};
  }
  .videoPlayButton {
    position: absolute;
    top: ${px2rem(16)};
    left: ${px2rem(16)};
  }
`;

export const UploadedLink = styled.div`
  width: 100%;
  height: ${px2rem(56)};
`;

export const UploadedMediaPreview = styled.img`
  width: ${px2rem(56)};
  height: ${px2rem(56)};
  object-fit: cover;
`;

export const DeleteMediaButton = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

export const MediaLoadingDotWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const LinkPreviewContainer = styled.div`
  width: 100%;
  height: ${px2rem(56)};
`;

const LinkPreviewAnchor = styled.a`
  display: block;
  width: 100%;
  height: 100%;
`;

const LinkPreviewContentContainer = styled.div`
  border: solid ${px2rem(1)} ${props => props.theme.grey300};
  border-radius: ${px2rem(4)};
  display: flex;
  height: 100%;
  background: ${props => props.theme.white100};
  overflow: hidden;
`;

const LinkPreviewContent = styled.div`
  position: relative;
  flex: 1;
  padding: ${px2rem(8)};
`;

const LinkPreviewImage = styled.div`
  flex: 0 0 ${px2rem(56)};
  background-position: center;
  background-size: cover;
`;

const LinkPreviewUrl = styled.div`
  position: absolute;
  bottom: ${px2rem(8)};
  color: ${props => props.theme.grey700};
  font-size: ${px2rem(11)};
  font-weight: ${props => props.theme.weightRegular};
  line-height: ${px2rem(14)};

  display: flex;
  align-items: center;

  svg {
    margin-left: ${px2rem(4)};
  }
`;

const LinkPreviewTitle = styled.div`
  color: ${props => props.theme.grey1000};
  font-size: ${px2rem(13)};
  line-height: ${px2rem(18)};
  font-weight: ${props => props.theme.weightBold};
`;

export const ThreadMediaArea = styled.div`
  min-width: ${px2rem(32)};
  display: flex;
  align-self: flex-start;
`;

interface IProps {
  linkPreview: Vingle.ILinkPreview;
}

export const LinkPreview = ({ linkPreview }: IProps) => {
  let titleElement: React.ReactNode = null;
  let urlElement: React.ReactNode = null;
  let imageElement: React.ReactNode = null;

  if (linkPreview.title) {
    titleElement = (
      <LinkPreviewTitle>
        <ShavedText value={linkPreview.title} />
      </LinkPreviewTitle>
    );
  } // 28px per line

  if (linkPreview.url) {
    const host = url.parse(linkPreview.url).hostname;
    urlElement = (
      <LinkPreviewUrl>
        {host}
        <Icon icon={PACKS.LINKPREVIEW_ARROW} width={8} height={14} />
      </LinkPreviewUrl>
    );
  }
  if (linkPreview.image) {
    imageElement = (
      <LinkPreviewImage
        style={{ backgroundImage: `url(${linkPreview.image})` }}
      />
    );
  }

  return (
    <div>
      <LinkPreviewContainer>
        <LinkPreviewAnchor href={linkPreview.url} target="_blank">
          <LinkPreviewContentContainer>
            <LinkPreviewContent>
              {titleElement}
              {urlElement}
            </LinkPreviewContent>
            {imageElement}
          </LinkPreviewContentContainer>
        </LinkPreviewAnchor>
      </LinkPreviewContainer>
    </div>
  );
};
